import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import { useAuth } from "../../../../contexts/AuthContext";
import {
  ActionIconContainer,
  AddFormButton,
  CustomLoader,
} from "../../../../shared/components/TableElements";
import {
  FormContainer,
  FormGroupLabel,
  FormItem,
} from "../../../../shared/components/form/FormElements";

import { Card, CardBody } from "../../../../shared/components/Card";
import {
  convertToDays,
  CustomInput,
  Desc,
  TimeSelect,
} from "../../MaintenanceReminders/MaintenanceRemindersForm";
import SubscribeTooltipIcon from "../../../../shared/components/SubscribeTooltipIcon";
import Error from "@/shared/components/form/Error";
import {
  formInputStyles,
  formStyles,
} from "../../../../shared/constants/styles";
import {
  getDateFnsLocale,
  handleNegative,
  handleNumberInputOnWheelPreventChange,
} from "../../../../shared/helpers";
import FormField from "../../../../shared/components/form/FormField";
import ActionPopup from "../../../../shared/components/ActionPopup";
import { colorYellowNew } from "../../../../utils/palette";

const DocRenewalFromUserPopup = ({
  showPopup,
  setShowPopup,
  type,
  docName,
  userName,
  expiry,
  setExpiry,
  docRenewalData,
  setDocRenewalData,
  onAddReminder,
}) => {
  const { t } = useTranslation("common");
  const { tag } = useAuth();

  const users = useSelector((state) => state.users.list).filter(
    (u) => u.user_active
  );

  const [showRenewalDeleteWarningPopup, setShowRenewalDeleteWarningPopup] =
    useState(false);

  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm();

  const expiryDate = watch("expiryDate");
  const notifyMeDuration = watch("notifyMeDuration");
  const notifyMePeriod = watch("notifyMePeriod");
  const userEmail = watch("userEmail");
  const notes = watch("notes");

  const handleClose = () => {
    setShowPopup(false);
  };

  useEffect(() => {
    if (docRenewalData) {
      setValue("expiryDate", expiry);
      setValue("notifyMeDuration", docRenewalData.daysThreshold);
      setValue("notifyMePeriod", "days");
      setValue("userEmail", docRenewalData.userEmail);
      setValue("notes", docRenewalData.notes);
    }
  }, [showPopup]);

  //Initial Effect
  useEffect(() => {
    if (type !== "edit") {
      setValue("notifyMePeriod", "days");
    }
  }, []);

  const validateMaxNotifyMe = (notifyMeInDays) => {
    if (notifyMeInDays) {
      const notifyMeDate = new Date(expiryDate);
      notifyMeDate.setDate(notifyMeDate.getDate() - notifyMeInDays);
      return notifyMeDate > new Date();
    } else {
      return true;
    }
  };

  const addReminder = () => {
    const daysThreshold = notifyMeDuration
      ? convertToDays(notifyMeDuration, notifyMePeriod)
      : 0;
    if (daysThreshold > 0) {
      const notifyMeCheck = validateMaxNotifyMe(daysThreshold);
      if (!notifyMeCheck) {
        setError("notifyMeDuration", {
          type: "maxValue",
          message: t("less_than_current_date_err"),
        });
        return;
      } else {
        clearErrors("notifyMeDuration");
      }
    }

    if (!expiryDate) {
      setShowRenewalDeleteWarningPopup(true);
      return;
    }

    if (onAddReminder) {
      onAddReminder(expiryDate);
    }

    setExpiry(expiryDate);

    setDocRenewalData((prev) => ({
      ...prev,
      expiryDate,
      daysThreshold,
      userEmail,
      notes,
      isExpiryUpdated: true,
    }));

    setShowPopup(false);
  };

  const handleYes = () => {
    if (onAddReminder) {
      onAddReminder(expiryDate);
    }

    setExpiry(expiryDate);

    const daysThreshold = notifyMeDuration
      ? convertToDays(notifyMeDuration, notifyMePeriod)
      : 0;

    setDocRenewalData((prev) => ({
      ...prev,
      expiryDate,
      daysThreshold,
      userEmail,
      notes,
      isExpiryUpdated: true,
    }));

    setShowRenewalDeleteWarningPopup(false);
    setShowPopup(false);
  };

  return (
    <>
      {showRenewalDeleteWarningPopup && (
        <ActionPopup
          showPopup={showRenewalDeleteWarningPopup}
          setShowPopup={setShowRenewalDeleteWarningPopup}
          message={t("user_renewals_delete_warn_msg")}
          icon={{
            className: "fi fi-rr-triangle-warning",
            color: colorYellowNew,
          }}
          handleYes={handleYes}
          handleNo={() => {
            setShowRenewalDeleteWarningPopup(false);
            setShowPopup(false);
          }}
          yesBtnText={t("ok")}
          noBtnText={t("cancel")}
        />
      )}
      <Modal show={showPopup} onHide={handleClose} size="lg">
        <Modal.Header>
          <h4>
            {type === "edit" ? t("edit_user_renewal") : t("add_user_renewal")}
          </h4>
          <div className="d-flex justify-content-end">
            <ActionIconContainer onClick={handleClose}>
              <i className="fi fi-rr-cross-small" />
            </ActionIconContainer>
          </div>
        </Modal.Header>
        <Modal.Body className="m-0 p-0">
          <FormContainer className="m-0 p-0">
            <Container className="m-0 p-0">
              <Row>
                <Col
                //   md={user && document ? 8 : 12}
                //   style={{
                //     paddingRight: user && document ? "0" : "12px",
                //   }}
                >
                  <Card className="m-0">
                    <CardBody
                      style={{
                        overflowY: "scroll",
                        height: `${window.innerHeight - 210}px`,
                        padding: "0",
                        overflowX: "hidden",
                        margin: 0,
                        //height: "650px",
                        //scrollbarStyles,
                      }}
                    >
                      {/* User and Document Type */}
                      <Row className="px-3">
                        <Col>
                          <div>
                            <FormGroupLabel>{t("user")}</FormGroupLabel>

                            <p>{userName}</p>
                            {/* <Controller
                              name="user"
                              control={control}
                              //errors={errors}
                              render={({ field: { onChange, value, ref } }) => (
                                <Select
                                  options={users}
                                  //value={vehicles.find((c) => c.value === value)}
                                  value={value}
                                  onChange={onChange}
                                  placeholder={t("user_placeholder")}
                                  styles={formStyles}
                                />
                              )}
                              rules={{ required: t("required_err") }}
                            /> */}
                          </div>
                          {errors?.user && (
                            <Error error={errors.user.message} />
                          )}
                        </Col>
                        <Col>
                          <div>
                            <FormGroupLabel>{t("document")}</FormGroupLabel>
                            <p>{docName}</p>
                            {/* <Controller
                              name="document"
                              control={control}
                              errors={errors}
                              render={({ field: { onChange, value, ref } }) => (
                                <Select
                                  options={docsList}
                                  //value={serviceTasks.find((c) => c.value === value)}
                                  value={value}
                                  onChange={onChange}
                                  placeholder={t("doc_type")}
                                  styles={formStyles}
                                  isDisabled={user ? false : true}
                                />
                              )}
                              rules={{ required: t("required_err") }}
                            /> */}
                          </div>
                          {errors?.document && (
                            <Error error={errors.document.message} />
                          )}
                        </Col>
                      </Row>

                      <Row className="px-3">
                        <Col md={6}>
                          <FormGroupLabel>{t("expiry_date")}</FormGroupLabel>
                          <Controller
                            name="expiryDate"
                            defaultValue={null}
                            control={control}
                            render={({ field: { onChange, value, ref } }) => (
                              <DatePicker
                                //minDate={new Date()}
                                minDate={(() => {
                                  const tomorrow = new Date();
                                  tomorrow.setDate(tomorrow.getDate() + 1);
                                  return tomorrow;
                                })()}
                                selected={value}
                                onChange={(val) => {
                                  onChange(val);
                                  if (!val) {
                                    setValue("notifyMeDuration", "");
                                  }
                                }}
                                customInput={<CustomInput clearable={true} />}
                                placeholderText={t("date_tv")}
                                locale={getDateFnsLocale()}
                                dateFormat={"P"}
                                showMonthDropdown
                                showYearDropdown
                                isClearable
                              />
                            )}
                            rules={{
                              required: true,
                              min: {
                                value: new Date(),
                                message: t(
                                  "exp_date_less_than_current_date_err_msg"
                                ),
                              },
                            }}
                          />

                          {errors?.expiryDate && (
                            <Error error={errors.exp.message} />
                          )}
                        </Col>
                      </Row>

                      {/* Notification Settings */}
                      <Row className="px-3 pt-3">
                        <div className="d-flex align-items-center">
                          <h5>{t("notification_settings")}</h5>
                          {tag === "free-user" && (
                            <div
                              //className="mb-1"
                              style={{ marginBottom: "0.1rem" }}
                            >
                              <SubscribeTooltipIcon
                                message={t("subscribe_msg")}
                                handleClick={() => setShowSubscribePopup(true)}
                              />
                            </div>
                          )}
                        </div>

                        <Desc
                          style={{ paddingTop: "4px" }}
                          className="d-flex gap-1"
                        >
                          <span>{t("notifications_email_msg")}</span>
                          {tag === "free-user" && (
                            <span>{`(${t("paid_feature_str_sm")})`}</span>
                          )}
                        </Desc>

                        <FormGroupLabel>{t("notify_me")}</FormGroupLabel>
                        <Col md={6}>
                          <div>
                            <FormItem
                              disabled={!expiryDate || tag === "free-user"}
                            >
                              <FormField
                                name="notifyMeDuration"
                                control={control}
                                component="input"
                                type="number"
                                onKeyDown={handleNegative}
                                onWheel={handleNumberInputOnWheelPreventChange}
                                errors={errors}
                                disabled={!expiryDate || tag === "free-user"}
                                rules={{
                                  min: {
                                    value: 1,
                                    message: t("greater_than_zero_err"),
                                  },
                                  // validate: {
                                  //   maxValue: (v) =>
                                  //     validateMaxNotifyMeOT(v) ||
                                  //     t("greater_than_expiry_err"),
                                  // },
                                }}
                                defaultValue=""
                                //isAboveError={isAboveError}
                                placeholder={t("duration")}
                                style={formInputStyles}
                              />
                              <TimeSelect
                                {...register("notifyMePeriod")}
                                defaultValue="days"
                                disabled={!expiryDate || tag === "free-user"}
                                onClick={() => {
                                  setError("notifyMeDuration", {
                                    type: "maxValue",
                                  });
                                }}
                              >
                                <option value="days">{t("days")}</option>
                                <option value="weeks">{t("weeks")}</option>
                                <option value="months">{t("months")}</option>
                                <option value="years">{t("years")}</option>
                              </TimeSelect>
                            </FormItem>
                          </div>
                          {errors?.notifyMeDuration && (
                            <Error error={errors.notifyMeDuration.message} />
                          )}
                        </Col>
                        <Col
                          md={2}
                          className="d-flex justify-content-center align-items-center p-0 mx-3"
                        >
                          <span>{t("before_expiry_date")}</span>
                        </Col>
                      </Row>

                      <Row className="px-3">
                        <FormGroupLabel>
                          {t("send_notification_to")}
                          {/* <Tooltip text={t("premium_feature")}>
                                <DollarIcon className="fi fi-ss-usd-circle"></DollarIcon>
                              </Tooltip> */}
                        </FormGroupLabel>
                        <Col md={6}>
                          <Controller
                            name="userEmail"
                            control={control}
                            errors={errors}
                            render={({ field: { onChange, value, ref } }) => (
                              <Select
                                options={users}
                                value={value}
                                onChange={onChange}
                                placeholder={t("noti_recipients")}
                                styles={formStyles}
                                isMulti
                                closeMenuOnSelect={false}
                                //hideSelectedOptions={false}
                                // components={{
                                //   Option,
                                // }}
                                isDisabled={tag === "free-user"}
                              />
                            )}
                            // rules={{ required: true }}
                          />
                        </Col>
                      </Row>

                      <Row className="px-3 pt-3">
                        <Col>
                          <Controller
                            name="notes"
                            control={control}
                            errors={errors}
                            render={({ field: { onChange, value, ref } }) => (
                              <textarea
                                rows={2}
                                id="notes"
                                name="notes"
                                onChange={onChange}
                                value={value}
                                placeholder={t("email_preview_placeholder")}
                                disabled={tag === "free-user"}
                              ></textarea>
                            )}
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </FormContainer>
        </Modal.Body>
        <Modal.Footer>
          <AddFormButton
            id="save-form-button"
            type="button"
            onClick={addReminder}
          >
            {t("save")}
          </AddFormButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocRenewalFromUserPopup;
